import React, { useState, useContext, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress, 
  Alert,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Divider,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AuthContext from '../../contexts/AuthContext';
import ClearIcon from '@mui/icons-material/Clear';
import EnterNpiStep from './EnterNpiStep';
import ConfirmProviderDetailsStep from './ConfirmProviderDetailsStep';
import EnterPracticeNpiStep from './EnterPracticeNpiStep';
import ConfirmPracticeDetailsStep from './ConfirmPracticeDetailsStep';
import ReviewAndSubmitStep from './ReviewAndSubmitStep';
import InHouseCollectionStep from './InHouseCollectionStep';
import StepIconOutlined from '../StepIconOutlined';
// Static list of practice type options (for usePersonalNpi auto-select)
const practiceTypeOptions = [
  'Family Medicine', 'Internal Medicine', 'Pediatrics', 'Obstetrics & Gynecology',
  'General Practice', 'Geriatric Medicine', 'Emergency Medicine', 'Urgent Care Clinic/Center',
  'Nurse Practitioner (Primary Care)', 'Physician Assistant (Medical)',
  'Cardiology', 'Endocrinology Diabetes & Metabolism', 'Gastroenterology',
  'Hematology', 'Hematology & Oncology', 'Infectious Disease', 'Nephrology',
  'Pulmonary Disease', 'Rheumatology', 'Allergy & Immunology', 'Neurology',
  'General Surgery', 'Orthopedic Surgery', 'Urology', 'Critical Care Medicine'
];

const SetupPracticeWizard = ({ practiceRequest, onUpdate }) => {
  const { tokens } = useContext(AuthContext);
  const [step, setStep] = useState(() => {
    if (!practiceRequest?.provider_npi) {
      return 1; // Start at Enter NPI if no provider NPI
    }
    if (!practiceRequest?.practice_npi) {
      return 3; // Start at Enter Practice NPI if no practice NPI
    }
    return 5; // Start at Review and Submit if both NPIs exist
  });
  const [npi, setNpi] = useState(practiceRequest?.provider_npi || '');
  const [practiceNpi, setPracticeNpi] = useState(practiceRequest?.practice_npi || '');
  const [usePersonalNpi, setUsePersonalNpi] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [npiData, setNpiData] = useState(null);
  const [practiceNpiData, setPracticeNpiData] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [doingInHouse, setDoingInHouse] = useState(Boolean(practiceRequest?.practice_is_doing_in_house_collection));
  const [inHouseAgreed, setInHouseAgreed] = useState(practiceRequest?.agreement_key_for_in_house_collection === 'ifu');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [clearNpiDialog, setClearNpiDialog] = useState(false);
  const [clearPracticeNpiDialog, setClearPracticeNpiDialog] = useState(false);
  const [editedDetails, setEditedDetails] = useState({
    first_name: practiceRequest?.provider_first_name || '',
    last_name: practiceRequest?.provider_last_name || '',
    middle_name: practiceRequest?.provider_middle_name || '',
    name_prefix: practiceRequest?.provider_name_prefix || '',
    name_suffix: practiceRequest?.provider_name_suffix || '',
    credential: practiceRequest?.provider_credential || '',
    address_1: practiceRequest?.provider_street_1 || '',
    address_2: practiceRequest?.provider_street_2 || '',
    city: practiceRequest?.provider_city || '',
    state: practiceRequest?.provider_state || '',
    postal_code: practiceRequest?.provider_zip_code || '',
    provider_phone: practiceRequest?.provider_phone || '',
    provider_fax: practiceRequest?.provider_fax || ''
  });
  const [practiceDetails, setPracticeDetails] = useState({
    practice_name: practiceRequest?.practice_name || '',
    billing_street_1: practiceRequest?.billing_street_1 || '',
    billing_street_2: practiceRequest?.billing_street_2 || '',
    billing_city: practiceRequest?.billing_city || '',
    billing_state: practiceRequest?.billing_state || '',
    billing_zip_code: practiceRequest?.billing_zip_code || '',
    shipping_street_1: practiceRequest?.shipping_street_1 || '',
    shipping_street_2: practiceRequest?.shipping_street_2 || '',
    shipping_city: practiceRequest?.shipping_city || '',
    shipping_state: practiceRequest?.shipping_state || '',
    shipping_zip_code: practiceRequest?.shipping_zip_code || '',
    practice_phone: practiceRequest?.practice_phone || '',
    practice_fax: practiceRequest?.practice_fax || '',
    practice_email: practiceRequest?.practice_email || '',
    practice_website: practiceRequest?.practice_website || '',
    // Additional practice info
    // Additional practice info
    practice_dba: practiceRequest?.practice_dba || '',
    practice_dba: practiceRequest?.practice_dba || '',
    practice_ein: practiceRequest?.practice_ein || '',
    practice_state_tax_id: practiceRequest?.practice_state_tax_id || '',
    practice_request_tax_exemption: practiceRequest?.practice_request_tax_exemption || false,
    practice_type: practiceRequest?.practice_type || ''
  });
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';

  const steps = ['Enter NPI', 'Confirm provider details', 'Enter practice NPI', 'Collections on-site', 'Confirm practice details', 'Review and submit'];

  useEffect(() => {
    const loadNpiData = async () => {
      if (practiceRequest?.provider_npi) {
        setLoading(true);
        try {
          const response = await fetch(`${apiBase}/npi?number=${practiceRequest.provider_npi}`, {
            headers: { Authorization: `Bearer ${tokens.id_token}` },
          });
          const data = await response.json();

          if (!response.ok) {
            throw new Error('Failed to load NPI data');
          }

          if (data.Errors) {
            throw new Error(data.Errors[0].description);
          }

          if (data.result_count === 0) {
            throw new Error('No NPI found with that number');
          }

          const firstResult = data.results[0];
          if (firstResult.enumeration_type !== 'NPI-1') {
            throw new Error('Please enter a personal NPI number (NPI-1)');
          }

          setNpiData(firstResult);
          // Update edited details with NPI data if not already set from practiceRequest
          setEditedDetails(prev => ({
            ...prev,
            first_name: prev.first_name || firstResult.basic.first_name,
            last_name: prev.last_name || firstResult.basic.last_name,
            middle_name: prev.middle_name || firstResult.basic.middle_name || '',
            name_prefix: prev.name_prefix || firstResult.basic.name_prefix || '',
            name_suffix: prev.name_suffix || firstResult.basic.name_suffix || '',
            credential: prev.credential || firstResult.basic.credential || '',
            provider_phone: prev.provider_phone || firstResult.addresses?.[0]?.telephone_number || '',
            provider_fax: prev.provider_fax || firstResult.addresses?.[0]?.fax_number || '',
            address_1: prev.address_1 || firstResult.addresses?.[0]?.address_1 || '',
            address_2: prev.address_2 || firstResult.addresses?.[0]?.address_2 || '',
            city: prev.city || firstResult.addresses?.[0]?.city || '',
            state: prev.state || firstResult.addresses?.[0]?.state || '',
            postal_code: prev.postal_code || firstResult.addresses?.[0]?.postal_code || ''
          }));
        } catch (error) {
          setError(error.message);
        } finally {
          setLoading(false);
        }
      }
    };

    loadNpiData();
  }, [practiceRequest?.provider_npi, apiBase]);

  useEffect(() => {
    const loadPracticeNpiData = async () => {
      if (practiceRequest?.practice_npi) {
        setLoading(true);
        try {
          const response = await fetch(`${apiBase}/npi?number=${practiceRequest.practice_npi}`, {
            headers: { Authorization: `Bearer ${tokens.id_token}` },
          });
          const data = await response.json();

          if (!response.ok) {
            throw new Error('Failed to load practice NPI data');
          }

          if (data.Errors) {
            throw new Error(data.Errors[0].description);
          }

          if (data.result_count === 0) {
            throw new Error('No practice NPI found with that number');
          }

          const firstResult = data.results[0];
          if (firstResult.enumeration_type !== 'NPI-2' && firstResult.number !== practiceRequest.provider_npi) {
            throw new Error('Please enter a practice NPI number (NPI-2) or use your personal NPI');
          }

          setPracticeNpiData(firstResult);
          // Auto-populate corporate address from NPI record
          const addresses = firstResult.addresses || [];
          let mailing = addresses.find(a => a.address_purpose?.toUpperCase() === 'MAILING');
          if (!mailing && addresses.length > 0) mailing = addresses[0];
          setPracticeDetails(prev => ({
            ...prev,
            corporate_street_1: mailing?.address_1 || prev.corporate_street_1,
            corporate_street_2: mailing?.address_2 || prev.corporate_street_2,
            corporate_city: mailing?.city || prev.corporate_city,
            corporate_state: mailing?.state || prev.corporate_state,
            corporate_zip_code: mailing?.postal_code || prev.corporate_zip_code,
            // Initialize practice type from NPI taxonomy if not already set
            practice_type: prev.practice_type || (firstResult.taxonomies?.[0]?.desc || prev.practice_type),
            // Default email to user's if not present in NPI
            practice_email: firstResult.basic?.email || practiceRequest.user_email || prev.practice_email,
          }));
        } catch (error) {
          setError(error.message);
        } finally {
          setLoading(false);
        }
      }
    };

    loadPracticeNpiData();
  }, [practiceRequest?.practice_npi, practiceRequest?.provider_npi, apiBase]);

  const handleNpiSubmit = async () => {
    if (!npi) {
      setError('Please enter your NPI number');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBase}/npi?number=${npi}`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error('Failed to validate NPI');
      }

      if (data.Errors) {
        setError(data.Errors[0].description);
        return;
      }

      if (data.result_count === 0) {
        setError('No NPI found with that number');
        return;
      }

      const firstResult = data.results[0];
      if (firstResult.enumeration_type !== 'NPI-1') {
        setError('Please enter a personal NPI number (NPI-1)');
        return;
      }

      setNpiData(firstResult);
      // Initialize edited details with NPI data
      setEditedDetails({
        first_name: firstResult.basic.first_name,
        last_name: firstResult.basic.last_name,
        middle_name: firstResult.basic.middle_name || '',
        name_prefix: firstResult.basic.name_prefix || '',
        name_suffix: firstResult.basic.name_suffix || '',
        credential: firstResult.basic.credential || '',
        provider_phone: firstResult.addresses?.[0]?.telephone_number || '',
        provider_fax: firstResult.addresses?.[0]?.fax_number || '',
        address_1: firstResult.addresses?.[0]?.address_1 || '',
        address_2: firstResult.addresses?.[0]?.address_2 || '',
        city: firstResult.addresses?.[0]?.city || '',
        state: firstResult.addresses?.[0]?.state || '',
        postal_code: firstResult.addresses?.[0]?.postal_code || ''
      });
      setStep(2);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePracticeNpiSubmit = async () => {
    if (usePersonalNpi) {
      setPracticeNpiData(npiData);
      // Initialize practice details from provider's information
      setPracticeDetails({
        practice_name: `${editedDetails.first_name} ${editedDetails.last_name}`,
        // Corporate address (from provider)
        corporate_street_1: editedDetails.address_1,
        corporate_street_2: editedDetails.address_2,
        corporate_city: editedDetails.city,
        corporate_state: editedDetails.state,
        corporate_zip_code: editedDetails.postal_code,
        // Clear billing address (user can copy from corporate)
        billing_street_1: '',
        billing_street_2: '',
        billing_city: '',
        billing_state: '',
        billing_zip_code: '',
        // Shipping address
        shipping_street_1: editedDetails.address_1,
        shipping_street_2: editedDetails.address_2,
        shipping_city: editedDetails.city,
        shipping_state: editedDetails.state,
        shipping_zip_code: editedDetails.postal_code,
        practice_phone: editedDetails.provider_phone,
        practice_fax: editedDetails.provider_fax,
        practice_email: npiData?.basic?.email || practiceRequest.user_email || '',
        practice_website: '',
        // Additional practice info
        practice_dba: practiceDetails.practice_dba,
        practice_ein: '',
        practice_state_tax_id: practiceDetails.practice_state_tax_id || '',
        practice_request_tax_exemption: false,
        // Auto-select first practice type option if available
        practice_type: practiceTypeOptions.length > 0 ? practiceTypeOptions[0] : ''
      });
      setStep(4);
      return;
    }

    if (!practiceNpi) {
      setError('Please enter a practice NPI number');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBase}/npi?number=${practiceNpi}`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error('Failed to validate NPI');
      }

      if (data.Errors) {
        setError(data.Errors[0].description);
        return;
      }

      if (data.result_count === 0) {
        setError('No NPI found with that number');
        return;
      }

      const firstResult = data.results[0];
      if (firstResult.enumeration_type !== 'NPI-2') {
        setError('Please enter a practice NPI number (NPI-2)');
        return;
      }

      setPracticeNpiData(firstResult);
      // Initialize billing/shipping from NPI data
      const addresses = firstResult.addresses || [];
      let mailing = addresses.find(a => a.address_purpose?.toUpperCase() === 'MAILING');
      let location = addresses.find(a => a.address_purpose?.toUpperCase() === 'LOCATION');
      if (!mailing && addresses.length) mailing = addresses[0];
      if (!location && addresses.length) location = addresses[0];
      setPracticeDetails({
        practice_name: firstResult.basic.organization_name || '',
        // Corporate address (from NPI mailing)
        corporate_street_1: mailing?.address_1 || '',
        corporate_street_2: mailing?.address_2 || '',
        corporate_city: mailing?.city || '',
        corporate_state: mailing?.state || '',
        corporate_zip_code: mailing?.postal_code || '',
        // Clear billing address (user can copy from corporate)
        billing_street_1: '',
        billing_street_2: '',
        billing_city: '',
        billing_state: '',
        billing_zip_code: '',
        // Shipping address (from NPI location)
        shipping_street_1: location?.address_1 || '',
        shipping_street_2: location?.address_2 || '',
        shipping_city: location?.city || '',
        shipping_state: location?.state || '',
        shipping_zip_code: location?.postal_code || '',
        practice_phone: mailing?.telephone_number || '',
        practice_fax: mailing?.fax_number || '',
        practice_email: firstResult.basic?.email || practiceRequest.user_email || '',
        practice_website: '',
        // Additional practice info
        practice_dba: practiceDetails.practice_dba,
        practice_ein: '',
        practice_state_tax_id: practiceDetails.practice_state_tax_id || '',
        practice_request_tax_exemption: false,
        // Auto-select first taxonomy as practice type
        practice_type: firstResult.taxonomies && firstResult.taxonomies.length > 0
          ? firstResult.taxonomies[0].desc
          : ''
      });
      setStep(4);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDetailChange = (field) => (event) => {
    setEditedDetails(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Handle practice detail changes, including checkbox inputs
  const handlePracticeDetailChange = (field) => (event) => {
    const { type, value, checked } = event.target;
    const newValue = (type === 'checkbox') ? checked : value;
    setPracticeDetails(prev => ({
      ...prev,
      [field]: newValue
    }));
  };

  const validateProviderFields = () => {
    const errors = {};
    
    // Provider validation only (no address required at this step)
    if (!editedDetails.first_name) errors.first_name = 'First Name is required';
    if (!editedDetails.last_name) errors.last_name = 'Last Name is required';
    if (!editedDetails.provider_phone) errors.provider_phone = 'Phone Number is required';
    if (!editedDetails.provider_fax) errors.provider_fax = 'Fax Number is required';

    console.log('Provider validation errors:', errors);
    console.log('Edited details:', editedDetails);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePracticeFields = () => {
    const errors = {};
    
    // Practice validation only
    if (!practiceDetails.practice_name) errors.practice_name = 'Practice Name is required';
    // Corporate address is required
    if (!practiceDetails.corporate_street_1) errors.corporate_street_1 = 'Corporate Street Address is required';
    if (!practiceDetails.corporate_city) errors.corporate_city = 'Corporate City is required';
    if (!practiceDetails.corporate_state) errors.corporate_state = 'Corporate State is required';
    if (!practiceDetails.corporate_zip_code) errors.corporate_zip_code = 'Corporate ZIP Code is required';
    if (!practiceDetails.billing_street_1) errors.billing_street_1 = 'Billing Street Address is required';
    if (!practiceDetails.billing_city) errors.billing_city = 'Billing City is required';
    if (!practiceDetails.billing_state) errors.billing_state = 'Billing State is required';
    if (!practiceDetails.billing_zip_code) errors.billing_zip_code = 'Billing ZIP Code is required';
    if (!practiceDetails.shipping_street_1) errors.shipping_street_1 = 'Shipping Street Address is required';
    if (!practiceDetails.shipping_city) errors.shipping_city = 'Shipping City is required';
    if (!practiceDetails.shipping_state) errors.shipping_state = 'Shipping State is required';
    if (!practiceDetails.shipping_zip_code) errors.shipping_zip_code = 'Shipping ZIP Code is required';
    if (!practiceDetails.practice_ein) errors.practice_ein = 'EIN is required';
    // State Tax ID required only when requesting tax exemption
    if (practiceDetails.practice_request_tax_exemption && !practiceDetails.practice_state_tax_id) {
      errors.practice_state_tax_id = 'State Tax ID is required';
    }
    if (!practiceDetails.practice_fax) errors.practice_fax = 'Fax Number is required';
    if (!practiceDetails.practice_email) errors.practice_email = 'Email is required';
    if (!practiceDetails.practice_phone) errors.practice_phone = 'Practice Phone Number is required';

    console.log('Practice validation errors:', errors);
    console.log('Practice details:', practiceDetails);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateFields = () => {
    const errors = {};
    
    // Provider validation
    if (!editedDetails.first_name) errors.first_name = 'First Name is required';
    if (!editedDetails.last_name) errors.last_name = 'Last Name is required';
    if (!editedDetails.phone_number) errors.phone_number = 'Phone Number is required';
    if (!editedDetails.address_1) errors.address_1 = 'Street Address is required';
    if (!editedDetails.city) errors.city = 'City is required';
    if (!editedDetails.state) errors.state = 'State is required';
    if (!editedDetails.postal_code) errors.postal_code = 'Postal Code is required';

    // Practice validation
    if (!practiceDetails.practice_name) errors.practice_name = 'Practice Name is required';
    if (!practiceDetails.practice_street_1) errors.practice_street_1 = 'Practice Street Address is required';
    if (!practiceDetails.practice_city) errors.practice_city = 'Practice City is required';
    if (!practiceDetails.practice_state) errors.practice_state = 'Practice State is required';
    if (!practiceDetails.practice_zip_code) errors.practice_zip_code = 'Practice ZIP Code is required';
    if (!practiceDetails.practice_phone) errors.practice_phone = 'Practice Phone Number is required';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleContinue = () => {
    console.log('Continue button clicked');
    console.log('Current step:', step);
    console.log('Validation errors before check:', validationErrors);
    
    if (!validateProviderFields()) {
      console.log('Provider validation failed');
      // Find the first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      if (firstErrorField) {
        const base = firstErrorField.replace(/_/g, ' ').trim();
        const fieldName = base ? base.charAt(0).toUpperCase() + base.slice(1).toLowerCase() : '';
        setError(`Please fill in the required field: ${fieldName}`);
        
        // Scroll to the first error
        const firstErrorElement = document.querySelector('[data-error="true"]');
        console.log('First error element:', firstErrorElement);
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        setError('Please fill in all required fields');
      }
      return;
    }
    console.log('Provider validation passed, moving to step 3');
    setError(null); // Clear any previous errors
    setStep(3);
  };

  const handlePracticeContinue = () => {
    console.log('Practice Continue button clicked');
    console.log('Current step:', step);
    console.log('Validation errors before check:', validationErrors);
    
    if (!validatePracticeFields()) {
      console.log('Practice validation failed');
      // Find the first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      if (firstErrorField) {
        const base = firstErrorField.replace(/_/g, ' ').trim();
        const fieldName = base ? base.charAt(0).toUpperCase() + base.slice(1).toLowerCase() : '';
        setError(`Please fill in the required field: ${fieldName}`);
        
        // Scroll to the first error
        const firstErrorElement = document.querySelector('[data-error="true"]');
        console.log('First error element:', firstErrorElement);
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        setError('Please fill in all required fields');
      }
      return;
    }
    console.log('Practice validation passed, moving to step 6 (Review)');
    setError(null); // Clear any previous errors
    setStep(6);
  };

  const handleSaveForLater = async () => {
    try {
      const payload = {
        provider_npi: npiData?.number || npi || practiceRequest?.provider_npi,
        provider_first_name: editedDetails.first_name,
        provider_last_name: editedDetails.last_name,
        provider_middle_name: editedDetails.middle_name,
        provider_name_prefix: editedDetails.name_prefix,
        provider_name_suffix: editedDetails.name_suffix,
        provider_credential: editedDetails.credential,
        provider_street_1: editedDetails.address_1,
        provider_street_2: editedDetails.address_2,
        provider_city: editedDetails.city,
        provider_state: editedDetails.state,
        provider_zip_code: editedDetails.postal_code,
        provider_phone: editedDetails.provider_phone,
        provider_fax: editedDetails.provider_fax,
        practice_npi: practiceNpiData?.number || practiceNpi || practiceRequest?.practice_npi,
        practice_name: practiceDetails.practice_name,
        // Corporate address
        corporate_street_1: practiceDetails.corporate_street_1,
        corporate_street_2: practiceDetails.corporate_street_2,
        corporate_city: practiceDetails.corporate_city,
        corporate_state: practiceDetails.corporate_state,
        corporate_zip_code: practiceDetails.corporate_zip_code,
        // Billing
        billing_street_1: practiceDetails.billing_street_1,
        billing_street_2: practiceDetails.billing_street_2,
        billing_city: practiceDetails.billing_city,
        billing_state: practiceDetails.billing_state,
        billing_zip_code: practiceDetails.billing_zip_code,
        // Shipping
        shipping_street_1: practiceDetails.shipping_street_1,
        shipping_street_2: practiceDetails.shipping_street_2,
        shipping_city: practiceDetails.shipping_city,
        shipping_state: practiceDetails.shipping_state,
        shipping_zip_code: practiceDetails.shipping_zip_code,
        // Contact
        practice_phone: practiceDetails.practice_phone,
        practice_fax: practiceDetails.practice_fax,
        practice_email: practiceDetails.practice_email,
        practice_website: practiceDetails.practice_website,
        // Additional practice info
        practice_dba: practiceDetails.practice_dba,
        practice_state_tax_id: practiceDetails.practice_state_tax_id,
        // New fields
        practice_ein: practiceDetails.practice_ein,
        practice_request_tax_exemption: practiceDetails.practice_request_tax_exemption,
        practice_type: practiceDetails.practice_type,
        // In-house collection agreement
        practice_is_doing_in_house_collection: Boolean(doingInHouse && inHouseAgreed),
        agreement_key_for_in_house_collection: (doingInHouse && inHouseAgreed) ? 'ifu' : null,
      };
      const response = await fetch(`${apiBase}/users/${practiceRequest.user_id}/setup-practice-request`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.id_token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to save practice setup request');
      }

      // Mark save time to show success banner
      setLastSavedAt(new Date());
      console.log('Practice setup request saved successfully');
    } catch (error) {
      console.error('Error saving practice setup request:', error);
      setError(error.message);
    }
  };

  const handleSubmitForReview = async () => {
    const errors = {};
    
    // Provider validation
    if (!editedDetails.first_name) errors.first_name = 'First Name is required';
    if (!editedDetails.last_name) errors.last_name = 'Last Name is required';
    if (!editedDetails.provider_phone) errors.provider_phone = 'Phone Number is required';
    if (!editedDetails.address_1) errors.address_1 = 'Street Address is required';
    if (!editedDetails.city) errors.city = 'City is required';
    if (!editedDetails.state) errors.state = 'State is required';
    if (!editedDetails.postal_code) errors.postal_code = 'Postal Code is required';

    // Practice validation
    if (!practiceDetails.practice_name) errors.practice_name = 'Practice Name is required';
    // Corporate address validation
    if (!practiceDetails.corporate_street_1) errors.corporate_street_1 = 'Corporate Street Address is required';
    if (!practiceDetails.corporate_city) errors.corporate_city = 'Corporate City is required';
    if (!practiceDetails.corporate_state) errors.corporate_state = 'Corporate State is required';
    if (!practiceDetails.corporate_zip_code) errors.corporate_zip_code = 'Corporate ZIP Code is required';
    if (!practiceDetails.billing_street_1) errors.billing_street_1 = 'Billing Street Address is required';
    if (!practiceDetails.billing_city) errors.billing_city = 'Billing City is required';
    if (!practiceDetails.billing_state) errors.billing_state = 'Billing State is required';
    if (!practiceDetails.billing_zip_code) errors.billing_zip_code = 'Billing ZIP Code is required';
    if (!practiceDetails.shipping_street_1) errors.shipping_street_1 = 'Shipping Street Address is required';
    if (!practiceDetails.shipping_city) errors.shipping_city = 'Shipping City is required';
    if (!practiceDetails.shipping_state) errors.shipping_state = 'Shipping State is required';
    if (!practiceDetails.shipping_zip_code) errors.shipping_zip_code = 'Shipping ZIP Code is required';
    if (!practiceDetails.practice_phone) errors.practice_phone = 'Practice Phone Number is required';
    if (practiceDetails.practice_request_tax_exemption && !practiceDetails.practice_state_tax_id) {
      errors.practice_state_tax_id = 'State Tax ID is required';
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Get all missing field names
      const missingFields = Object.keys(errors)
        .map(field => {
          const base = field.replace(/_/g, ' ').trim();
          return base ? base.charAt(0).toUpperCase() + base.slice(1).toLowerCase() : '';
        });

      setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      
      // Scroll to the first error
      const firstErrorElement = document.querySelector('[data-error="true"]');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setError(null); // Clear any previous errors
    setLoading(true);

    try {
      const response = await fetch(`${apiBase}/users/${practiceRequest.user_id}/setup-practice-request`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.id_token}`
        },
        body: JSON.stringify({
          provider_npi: npiData.number,
          provider_first_name: editedDetails.first_name,
          provider_last_name: editedDetails.last_name,
          provider_middle_name: editedDetails.middle_name,
          provider_name_prefix: editedDetails.name_prefix,
          provider_name_suffix: editedDetails.name_suffix,
          provider_credential: editedDetails.credential,
          provider_street_1: editedDetails.address_1,
          provider_street_2: editedDetails.address_2,
          provider_city: editedDetails.city,
          provider_state: editedDetails.state,
          provider_zip_code: editedDetails.postal_code,
          provider_phone: editedDetails.provider_phone,
          provider_fax: editedDetails.provider_fax,
          practice_npi: practiceNpiData.number,
          practice_name: practiceDetails.practice_name,
          // Corporate address
          corporate_street_1: practiceDetails.corporate_street_1,
          corporate_street_2: practiceDetails.corporate_street_2,
          corporate_city: practiceDetails.corporate_city,
          corporate_state: practiceDetails.corporate_state,
          corporate_zip_code: practiceDetails.corporate_zip_code,
          billing_street_1: practiceDetails.billing_street_1,
          billing_street_2: practiceDetails.billing_street_2,
          billing_city: practiceDetails.billing_city,
          billing_state: practiceDetails.billing_state,
          billing_zip_code: practiceDetails.billing_zip_code,
          shipping_street_1: practiceDetails.shipping_street_1,
          shipping_street_2: practiceDetails.shipping_street_2,
          shipping_city: practiceDetails.shipping_city,
          shipping_state: practiceDetails.shipping_state,
          shipping_zip_code: practiceDetails.shipping_zip_code,
          practice_phone: practiceDetails.practice_phone,
          practice_fax: practiceDetails.practice_fax,
          practice_email: practiceDetails.practice_email,
          practice_website: practiceDetails.practice_website,
          // Additional practice info
          practice_dba: practiceDetails.practice_dba,
          practice_state_tax_id: practiceDetails.practice_state_tax_id,
          // New fields
          practice_ein: practiceDetails.practice_ein,
          practice_request_tax_exemption: practiceDetails.practice_request_tax_exemption,
          practice_type: practiceDetails.practice_type,
          // In-house collection agreement
          practice_is_doing_in_house_collection: Boolean(doingInHouse && inHouseAgreed),
          agreement_key_for_in_house_collection: (doingInHouse && inHouseAgreed) ? 'ifu' : null,
          status: 'Submitted'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit practice setup request for review');
      }

      const updatedRequest = await response.json();
      onUpdate(updatedRequest);
    } catch (error) {
      console.error('Error submitting practice setup request:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClearNpi = () => {
    setNpi('');
    setNpiData(null);
    setEditedDetails({
      first_name: '',
      last_name: '',
      middle_name: '',
      name_prefix: '',
      name_suffix: '',
      credential: '',
      address_1: '',
      address_2: '',
      city: '',
      state: '',
      postal_code: '',
      provider_phone: '',
      provider_fax: ''
    });
    setStep(1);
    setClearNpiDialog(false);
  };

  const handleClearPracticeNpi = () => {
    setPracticeNpi('');
    setPracticeNpiData(null);
    setPracticeDetails({
      practice_name: '',
      // Corporate Address
      corporate_street_1: '',
      corporate_street_2: '',
      corporate_city: '',
      corporate_state: '',
      corporate_zip_code: '',
      // Billing Address
      billing_street_1: '',
      billing_street_2: '',
      billing_city: '',
      billing_state: '',
      billing_zip_code: '',
      // Shipping Address
      shipping_street_1: '',
      shipping_street_2: '',
      shipping_city: '',
      shipping_state: '',
      shipping_zip_code: '',
      // Contact
      practice_phone: '',
      practice_fax: '',
      practice_email: '',
      practice_website: '',
      // New fields
      practice_dba: '',
      practice_state_tax_id: '',
      practice_tax_classification: '',
      practice_type: ''
    });
    setStep(3);
    setClearPracticeNpiDialog(false);
  };
  // Copy billing address fields into shipping address
  const handleCopyBilling = () => {
    setPracticeDetails(prev => ({
      ...prev,
      shipping_street_1: prev.billing_street_1,
      shipping_street_2: prev.billing_street_2,
      shipping_city: prev.billing_city,
      shipping_state: prev.billing_state,
      shipping_zip_code: prev.billing_zip_code,
    }));
  };
  // Copy corporate address fields into billing address
  const handleCopyCorporateToBilling = () => {
    setPracticeDetails(prev => ({
      ...prev,
      billing_street_1: prev.corporate_street_1,
      billing_street_2: prev.corporate_street_2,
      billing_city: prev.corporate_city,
      billing_state: prev.corporate_state,
      billing_zip_code: prev.corporate_zip_code,
    }));
  };
  // Copy corporate address fields into shipping address
  const handleCopyCorporateToShipping = () => {
    setPracticeDetails(prev => ({
      ...prev,
      shipping_street_1: prev.corporate_street_1,
      shipping_street_2: prev.corporate_street_2,
      shipping_city: prev.corporate_city,
      shipping_state: prev.corporate_state,
      shipping_zip_code: prev.corporate_zip_code,
    }));
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <EnterNpiStep
            npi={npi}
            error={error}
            loading={loading}
            onChange={(e) => setNpi(e.target.value)}
            onSubmit={handleNpiSubmit}
            onOpenClearDialog={() => setClearNpiDialog(true)}
          />
        );
      case 2:
        return (
          <ConfirmProviderDetailsStep
            npiData={npiData}
            details={editedDetails}
            errors={validationErrors}
            onDetailChange={handleDetailChange}
            onBack={handleClearNpi}
            onContinue={handleContinue}
          />
        );
      case 3:
        return (
          <EnterPracticeNpiStep
            usePersonalNpi={usePersonalNpi}
            practiceNpi={practiceNpi}
            error={error}
            loading={loading}
            onToggleUsePersonal={(checked) => setUsePersonalNpi(checked)}
            onPracticeNpiChange={(e) => setPracticeNpi(e.target.value)}
            onValidate={handlePracticeNpiSubmit}
            onBack={() => setStep(2)}
            onOpenClearDialog={() => setClearPracticeNpiDialog(true)}
          />
        );
      case 4:
        return (
          <InHouseCollectionStep
            value={doingInHouse}
            agreed={inHouseAgreed}
            onChange={setDoingInHouse}
            onAgreeChange={setInHouseAgreed}
            onBack={() => setStep(3)}
            onContinue={() => setStep(5)}
          />
        );
      case 5:
        return (
          <ConfirmPracticeDetailsStep
            practiceNpiData={practiceNpiData}
            details={practiceDetails}
            errors={validationErrors}
            onDetailChange={handlePracticeDetailChange}
            onCopyBilling={handleCopyBilling}
            onCopyCorporateToBilling={handleCopyCorporateToBilling}
            onCopyCorporateToShipping={handleCopyCorporateToShipping}
            onBack={() => setStep(4)}
            onContinue={handlePracticeContinue}
          />
        );
      case 6:
        return (
          <ReviewAndSubmitStep
            npiData={npiData}
            practiceNpiData={practiceNpiData}
            editedDetails={editedDetails}
            practiceDetails={practiceDetails}
            errors={validationErrors}
            loading={loading}
            onDetailChange={handleDetailChange}
            onPracticeDetailChange={handlePracticeDetailChange}
            onBack={() => setStep(5)}
            onSubmit={handleSubmitForReview}
            onOpenClearProviderDialog={() => setClearNpiDialog(true)}
            onOpenClearPracticeDialog={() => setClearPracticeNpiDialog(true)}
            doingInHouse={doingInHouse}
            inHouseAgreed={inHouseAgreed}
            onChangeDoingInHouse={setDoingInHouse}
            onChangeInHouseAgreed={setInHouseAgreed}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Card>
        <CardContent>
        <Stepper activeStep={step - 1} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel StepIconComponent={StepIconOutlined}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mt: 2 }}>
          {renderStep()}
        </Box>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleSaveForLater}>
              Save for Later
            </Button>
          </Box>
          {lastSavedAt && (
            <Alert severity="success" sx={{ mt: 1 }}>
              Saved at {lastSavedAt.toLocaleString()}.
            </Alert>
          )}
        </Box>
        </CardContent>
      </Card>

      <Dialog open={clearNpiDialog} onClose={() => setClearNpiDialog(false)}>
        <DialogTitle>Clear provider information</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will clear your provider NPI and all entered provider details. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearNpiDialog(false)}>Cancel</Button>
          <Button color="error" onClick={handleClearNpi}>Clear</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={clearPracticeNpiDialog} onClose={() => setClearPracticeNpiDialog(false)}>
        <DialogTitle>Clear practice information</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will clear your practice NPI and all entered practice details. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearPracticeNpiDialog(false)}>Cancel</Button>
          <Button color="error" onClick={handleClearPracticeNpi}>Clear</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SetupPracticeWizard;
